import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { knowledgeCategories } from '../../../mocks/knowledgeData';

export default function HeroSection() {
  const navigate = useNavigate();
  const [hoveredTree, setHoveredTree] = useState<string | null>(null);

  const handleTreeClick = (categoryId: string) => {
    navigate(`/knowledge/${categoryId}`);
  };

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/2571bb97e479ebebb1e75604326e5f40.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-sky-50/80 via-white/70 to-emerald-50/60 z-0"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
        <div className="mb-8 inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-emerald-200 shadow-sm">
          <i className="ri-sparkling-line text-emerald-600"></i>
          <span className="text-sm font-medium text-emerald-700">智能财税知识库</span>
        </div>

        <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-4 leading-tight">
          财税知识森林
        </h1>
        
        <p className="text-xl text-gray-600 mb-16 max-w-3xl mx-auto leading-relaxed">
          点击每一棵知识树，探索财税领域的智慧宝藏
        </p>

        {/* 森林区域 */}
        <div className="relative max-w-6xl mx-auto">
          {/* 地面 */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-emerald-100/60 to-transparent rounded-full blur-xl"></div>
          
          {/* 树木网格 */}
          <div className="relative grid grid-cols-5 gap-8 items-end justify-items-center pb-12">
            {knowledgeCategories.map((category, index) => {
              const isHovered = hoveredTree === category.id;
              const treeHeights = ['h-48', 'h-56', 'h-52', 'h-60', 'h-54'];
              const treeHeight = treeHeights[index % treeHeights.length];
              
              return (
                <div
                  key={category.id}
                  className="relative cursor-pointer group"
                  onClick={() => handleTreeClick(category.id)}
                  onMouseEnter={() => setHoveredTree(category.id)}
                  onMouseLeave={() => setHoveredTree(null)}
                  style={{ 
                    animationDelay: `${index * 150}ms`,
                    transform: isHovered ? 'scale(1.1) translateY(-10px)' : 'scale(1)',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                  }}
                >
                  {/* 树木主体 */}
                  <div className={`relative ${treeHeight} w-32 flex flex-col items-center justify-end`}>
                    {/* 树冠 */}
                    <div className="relative w-full flex flex-col items-center">
                      {/* 顶层树叶 */}
                      <div 
                        className="w-20 h-20 rounded-full transition-all duration-300"
                        style={{ 
                          backgroundColor: isHovered ? category.color : `${category.color}dd`,
                          boxShadow: isHovered ? `0 8px 30px ${category.color}40` : `0 4px 15px ${category.color}20`
                        }}
                      >
                        <div className="w-full h-full flex items-center justify-center">
                          <i 
                            className={`${category.icon} text-3xl text-white transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`}
                          ></i>
                        </div>
                      </div>
                      
                      {/* 中层树叶 */}
                      <div 
                        className="w-28 h-24 rounded-full -mt-6 transition-all duration-300"
                        style={{ 
                          backgroundColor: isHovered ? `${category.color}ee` : `${category.color}cc`,
                          boxShadow: isHovered ? `0 6px 25px ${category.color}30` : `0 3px 12px ${category.color}15`
                        }}
                      ></div>
                      
                      {/* 底层树叶 */}
                      <div 
                        className="w-32 h-20 rounded-full -mt-4 transition-all duration-300"
                        style={{ 
                          backgroundColor: isHovered ? `${category.color}dd` : `${category.color}bb`,
                          boxShadow: isHovered ? `0 4px 20px ${category.color}25` : `0 2px 10px ${category.color}10`
                        }}
                      ></div>
                    </div>
                    
                    {/* 树干 */}
                    <div className="w-6 h-16 bg-gradient-to-b from-amber-700 to-amber-800 rounded-t-lg -mt-2 relative z-10">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                    </div>
                  </div>

                  {/* 树木标签 */}
                  <div 
                    className={`absolute -bottom-16 left-1/2 transform -translate-x-1/2 w-40 transition-all duration-300 ${
                      isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                    }`}
                  >
                    <div 
                      className="bg-white rounded-xl shadow-lg p-3 border-2"
                      style={{ borderColor: category.color }}
                    >
                      <div className="text-sm font-bold text-gray-900 mb-1">{category.name}</div>
                      <div className="text-xs text-gray-600 leading-tight">{category.description}</div>
                      <div className="mt-2 flex items-center justify-center gap-1 text-xs font-medium" style={{ color: category.color }}>
                        <span>点击探索</span>
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                    {/* 箭头 */}
                    <div 
                      className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 rotate-45 border-t-2 border-l-2 bg-white"
                      style={{ borderColor: category.color }}
                    ></div>
                  </div>

                  {/* 树木阴影 */}
                  <div 
                    className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-4 rounded-full transition-all duration-300"
                    style={{ 
                      backgroundColor: `${category.color}15`,
                      filter: 'blur(8px)',
                      transform: isHovered ? 'translateX(-50%) scale(1.2)' : 'translateX(-50%) scale(1)'
                    }}
                  ></div>
                </div>
              );
            })}
          </div>

          {/* 提示文字 */}
          <div className="mt-24 flex items-center justify-center gap-3 text-gray-600">
            <i className="ri-hand-finger-line text-2xl animate-bounce"></i>
            <span className="text-sm font-medium">点击任意知识树开始探索</span>
          </div>
        </div>

        {/* 统计信息 */}
        <div className="mt-20 grid grid-cols-3 gap-8 max-w-3xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="text-3xl font-bold text-emerald-600 mb-2">2000+</div>
            <div className="text-sm text-gray-600">法规文件</div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="text-3xl font-bold text-emerald-600 mb-2">500+</div>
            <div className="text-sm text-gray-600">实务案例</div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="text-3xl font-bold text-emerald-600 mb-2">每日更新</div>
            <div className="text-sm text-gray-600">最新资讯</div>
          </div>
        </div>
      </div>
    </section>
  );
}